// Mixpedia deep-link routes → search arguments.
//
// Every entity page has a human-readable URL (`/mix/<dj>/<title>`,
// `/track/<artist>/<title>`, `/artist/<name>`) whose slugs are LOSSY: they
// lowercase, collapse whitespace, and turn real hyphens into separators. They
// exist for humans and for SEO — they are not a key.
//
// The key is `?id=`, written by App.js's URL effect from the id the page was
// opened with. Restoring by id is what the search thunks want anyway:
// searchTrack calls an id seed "exact (no normalized-name miss, no dupe-row
// shadowing)" and ships a `track_page_seed_mismatch` tripwire because the
// text path once served the wrong track for a week.
//
// Text is the fallback for links minted before ids were carried. It must use
// EVERY slug segment: searching `{ title: <dj segment> }` and discarding the
// title is what turned a refresh of one mix page at KHIDI into a list of
// every KHIDI mix.
//
// These are pure so they can be tested without mounting App.

// decodeURIComponent THROWS on malformed input ("%E0%A4%A", a truncated
// paste, a hand-typed URL). These parsers run inside the deep-link and
// popstate restores, where a throw takes the whole navigation down — so a bad
// slug degrades to its raw text rather than breaking the page.
export const fromSlug = (s) => {
    const raw = String(s ?? '');
    let decoded;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        decoded = raw;
    }
    return decoded.replace(/-/g, ' ').trim();
};

const idFrom = (searchStr) => {
    try {
        return new URLSearchParams(searchStr || '').get('id') || null;
    } catch {
        return null;
    }
};

// `/mix/<dj>/<title>` → { query, args } for dispatch(searchMixes(args)).
// `m` is the RegExp match: [full, djSlug, titleSlug].
export const mixRouteToSearch = (m, searchStr) => {
    const publicId = idFrom(searchStr);
    const dj = fromSlug(m[1]);
    const title = fromSlug(m[2]);
    const query = title ? `${dj} - ${title}` : dj;
    return {
        query,
        // fallbackToTitle: a DJ name that didn't survive slugging (real
        // hyphens, e.g. "Jean-Michel Jarre") matches no artist row, so retry
        // on title rather than land the user on an empty page.
        args: publicId
            ? { filters: { public_id: publicId } }
            : { filters: { artist: dj, title }, fallbackToTitle: true },
    };
};

// `/artist/<name>` → args for dispatch(searchArtist(args)).
export const artistRouteToSearch = (m, searchStr) => ({
    artistName: fromSlug(m[1]),
    artistId: idFrom(searchStr) || undefined,
});

// `/track/<artist>/<title>` → args for dispatch(searchTrack(args)).
export const trackRouteToSearch = (m, searchStr) => ({
    trackName: `${fromSlug(m[1])} - ${fromSlug(m[2])}`,
    trackId: idFrom(searchStr) || undefined,
});

// /mix/<artist-slug>?filter=by|featuring[&id=<artist public id>] → the
// searchMixes filters + the mix-filter context's artistPublicId. The id is
// written by App.js's URL writer from mixFilterContext.artistPublicId; a
// restore (reload, back/forward, deep link) must filter by it, not by the
// slugged name alone, which can resolve to a namesake (Karotte → Kaiserdisco).
// Numeric / empty ids are not catalogue ids and are dropped.
// An event / venue / source page URL: /mix/<name>?filter=source&id=<source id>
// (a club / festival / radio / podcast page — mixes by relation) or
// /mix/<name>?filter=title (a mix-title hub). Null for the artist filters.
export const eventPageFromUrl = (filter, name, rawId) => {
    if (filter === 'source' && typeof rawId === 'string' && rawId.trim() && !/^\d+$/.test(rawId.trim())) {
        const id = rawId.trim();
        return { context: { kind: 'title', titleQuery: name, eventKind: 'event', sourceId: id, covers: [], mixCount: null }, args: { filters: { source: id }, paginate: true, lite: true } };
    }
    if (filter === 'title' && name) {
        return { context: { kind: 'title', titleQuery: name, eventKind: 'event', covers: [], mixCount: null }, args: { filters: { title: name }, paginate: true, lite: true } };
    }
    return null;
};

export const mixFilterFiltersFromUrl = (kind, artistName, rawId) => {
    const id = typeof rawId === 'string' && rawId.trim() && !/^\d+$/.test(rawId.trim()) ? rawId.trim() : null;
    const base = kind === 'featuring' ? { artistIncluded: artistName } : { artist: artistName };
    return {
        filters: id ? { ...base, artist_id: id } : base,
        artistPublicId: id,
    };
};
